<script lang="ts" setup>
import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import KUICoreLayoutSidebar from '~kui/components/KUI/Core/Layout/Sidebar/KUICoreLayoutSidebar.vue';

import type { SidebarNavItem } from './types';

import KUISchoolsLayoutSidebarSchool from './KUISchoolsLayoutSidebarSchool.vue';

interface Props {
  navItems: SidebarNavItem[];
  accountLabel: string;
  logoutLabel: string;
  accountHref?: string;
  logoHref?: string;
  logoText: string;
  sidebarLabel: string;
  primaryNavLabel: string;
  closeLabel: string;
  promoDismissLabel?: string;
  dismissiblePromo?: boolean;
  schoolName?: string;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  accountHref: undefined,
  logoHref: undefined,
  promoDismissLabel: undefined,
  dismissiblePromo: false,
  schoolName: undefined,
  loading: false,
});

const emit = defineEmits<{
  logout: [];
  dismissPromo: [];
}>();
const isOpen = defineModel<boolean>('isOpen', { default: false });

const NuxtLink = defineNuxtLink({});

const navItemComponent = (item: SidebarNavItem) => (item.to ? NuxtLink : 'button');
const accountComponent = computed(() => (props.accountHref ? NuxtLink : 'button'));

const navItemClasses = (active: boolean) =>
  twMerge(
    cva(
      'flex items-center gap-2 w-full px-6 py-4 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ',
      {
        variants: {
          active: {
            true: 'bg-background font-semibold',
            false: 'hover:bg-accent/50',
          },
        },
      },
    )({ active }),
  );
</script>

<template>
  <KUICoreLayoutSidebar
    v-model:open="isOpen"
    mode="drawer"
    width-class="w-[260px]"
    :label="sidebarLabel"
    test-id="schools-sidebar"
    backdrop-test-id="schools-sidebar-backdrop"
  >
    <template v-if="!loading" #header>
      <div class="border-b border-border">
        <div class="flex items-center justify-between gap-4 p-4">
          <component
            :is="logoHref ? NuxtLink : 'div'"
            :to="logoHref"
            class="inline-flex items-center"
            :aria-label="logoText"
            data-testid="schools-sidebar-logo"
          >
            <KUICoreIllustration illustration="anyway-logo" class="h-8 w-auto text-foreground" />
          </component>
          <Button
            variant="ghost"
            size="icon-sm"
            :aria-expanded="isOpen"
            aria-controls="schools-sidebar-content"
            :aria-label="closeLabel"
            data-testid="schools-sidebar-close"
            @click="isOpen = false"
          >
            <Icon :name="ICONS.arrowLeft" size="sm" />
          </Button>
        </div>
        <div
          v-if="schoolName"
          class="px-4 pb-4"
          data-testid="schools-sidebar-school"
        >
          <slot name="school">
            <KUISchoolsLayoutSidebarSchool :school-name="schoolName" />
          </slot>
        </div>
      </div>
    </template>

    <KUICoreContainerCentered v-if="loading" class="h-full">
      <KUICoreLoaderAnyway size="md" />
    </KUICoreContainerCentered>

    <nav
      v-else
      id="schools-sidebar-content"
      class="py-3"
      :aria-label="primaryNavLabel"
    >
      <ul class="flex flex-col gap-px">
        <li
          v-for="(item, i) in navItems"
          :key="item.label + i"
          data-testid="schools-sidebar-nav-item"
        >
          <component
            :is="navItemComponent(item)"
            :to="item.to"
            :target="item.target"
            :type="item.to ? undefined : 'button'"
            :class="navItemClasses(item.isActive ?? false)"
            :data-testid="item.testId"
            :aria-current="item.isActive ? 'page' : undefined"
            @click="item.onClick?.()"
          >
            <Icon
              v-if="item.iconPath"
              :name="item.iconPath"
              size="sm"
              class="shrink-0"
            />
            <span class="flex-1 text-left">{{ item.label }}</span>
            <span
              v-if="item.badgeCount"
              class="ml-auto rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground"
              data-testid="schools-sidebar-badge"
            >
              {{ item.badgeCount }}
            </span>
          </component>
        </li>
      </ul>
    </nav>

    <template v-if="!loading" #footer>
      <div
        v-if="$slots.promo"
        class="relative mx-2 mb-3 mt-4"
        data-testid="schools-sidebar-promo"
      >
        <Button
          v-if="dismissiblePromo"
          variant="outline"
          size="icon-sm"
          class="absolute z-10 right-2 top-2"
          :aria-label="promoDismissLabel"
          data-testid="schools-sidebar-promo-dismiss"
          @click="emit('dismissPromo')"
        >
          <Icon :name="ICONS.cancel" size="xs" />
        </Button>
        <slot name="promo" />
      </div>

      <div class="border-t border-border py-2">
        <component
          :is="accountComponent"
          :to="accountHref"
          :type="accountHref ? undefined : 'button'"
          class="flex items-center gap-2 w-full px-6 py-1.5 text-xs hover:bg-accent/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          data-testid="schools-sidebar-account"
        >
          <Icon :name="ICONS.userCircleAlt" size="sm" />
          <span>{{ accountLabel }}</span>
        </component>
        <button
          type="button"
          class="flex items-center gap-2 w-full px-6 py-1.5 text-xs hover:bg-accent/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          data-testid="schools-sidebar-logout"
          @click="emit('logout')"
        >
          <Icon :name="ICONS.logoutCircle" size="sm" />
          <span>{{ logoutLabel }}</span>
        </button>
      </div>
    </template>
  </KUICoreLayoutSidebar>
</template>
